import React, { useState } from "react";
import {
  Table,
  Form,
  Breadcrumb,
  Button,
  Spinner,
  Modal,
  Row,
  Col,
  Tab,
  Tabs,
} from "react-bootstrap";
import useReactRouter from "use-react-router";
import axios from "axios";
import {
  faEdit,
  faSave,
  faTimes,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import Switch from "react-switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  COLOR_APP,
  COLOR_APP_CANCEL,
  LINK_SALE_PAGE,
  S3_URL,
} from "../../constants";
import { useToasts } from "react-toast-notifications";
import { v4 as uuidv4 } from "uuid";
import { GET_ADVERTISINGS } from "../../graphql/setting/query";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  CREATE_ADVERTISING,
  DELETE_ADVERTISING,
  UPDATE_ADVERTISING,
} from "../../graphql/setting/mutation";
import { GET_PRESIGN_URL } from "../../graphql/presignUrl/query";
import useWindowDimensions from "../../helpers/useWindowDimensions";
import moment from "moment";
import { MdAdd } from "react-icons/md";
import { BsLink } from "react-icons/bs";
import copy from "clipboard-copy";
import ModalConfirmComponent from "../../components/ModalConfirmComponent";
import LoadingSpinner from "../../components/LoadingSpinner";

// ★ UI ແບບດຽວກັບ POS (settings/banner) — ແຍກ tab ຈໍໃຫຍ່ / ໂທລະສັບ + checkbox ບໍ່ກຳນົດວັນທີ
// status = false ຄື "ເປີດ" (ສະແດງ), status = true ຄື "ປິດ"
// device = "MOBILE" ຄື banner ຈໍນ້ອຍ (16:9), ບໍ່ມີຄ່າ / "DESKTOP" ຄື ຈໍໃຫຍ່ (ຂໍ້ມູນເກົ່າທັງໝົດ)
const DEVICE_MOBILE = "MOBILE";
const DEVICE_DESKTOP = "DESKTOP";

// 1 section = banner 1 ປະເພດໜ້າຈໍ — ທັງສອງ section ໃຊ້ query ດຽວກັນ (Apollo cache ຮ່ວມກັນ)
// ແລ້ວແຍກຕາມ device ຝັ່ງໜ້າເວັບ ເພາະ banner ເກົ່າບໍ່ມີ device
function AdvertisingSection({ device, title, sizeHint, shopId, width }) {
  const { addToast } = useToasts();
  const isMobileSection = device === DEVICE_MOBILE;
  const previewAspect = isMobileSection ? "16 / 9" : undefined;

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [adverName, setAdverName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // ບໍ່ກຳນົດວັນທີ → ບັນທຶກ startDate / endDate ເປັນ null
  const [noDateLimit, setNoDateLimit] = useState(false);
  const [adverNote, setAdverNote] = useState("");
  const [file, setFile] = useState();
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState();

  const showError = (error) =>
    addToast(error?.message || "ເກີດຂໍ້ຜິດພາດ", {
      appearance: "error",
      autoDismiss: true,
    });
  const showSuccess = (text) =>
    addToast(text, { appearance: "success", autoDismiss: true });
  const showWarning = (text) =>
    addToast(text, { appearance: "warning", autoDismiss: true });

  const { data, loading, refetch } = useQuery(GET_ADVERTISINGS, {
    variables: { where: { shop: shopId || "" } },
    skip: !shopId,
    fetchPolicy: "cache-and-network",
  });
  const [createAdvertising] = useMutation(CREATE_ADVERTISING);
  const [updateAdvertising] = useMutation(UPDATE_ADVERTISING);
  const [deleteAdvertising, { loading: deleteLoading }] =
    useMutation(DELETE_ADVERTISING);
  const [getPresignUrl] = useLazyQuery(GET_PRESIGN_URL);

  const banners = (data?.advertisements?.data || []).filter((banner) =>
    isMobileSection
      ? banner?.device === DEVICE_MOBILE
      : banner?.device !== DEVICE_MOBILE,
  );

  // ອັບໂຫຼດຮູບຂຶ້ນ S3 ແລ້ວສົ່ງຊື່ໄຟລ໌ກັບ (ລໍໃຫ້ອັບໂຫຼດແລ້ວກ່ອນຈຶ່ງບັນທຶກ)
  const uploadImage = async (imageFile) => {
    const ext = imageFile.name.split(".").pop();
    const filename = uuidv4() + "." + ext;
    const presignResponse = await getPresignUrl({
      variables: {
        name: filename,
      },
    });
    const uploadUrl = presignResponse?.data?.preSignedUrl?.url;
    if (!uploadUrl) throw new Error("ອັບໂຫຼດຮູບບໍ່ສຳເລັດ");
    await axios.put(uploadUrl, imageFile, {
      headers: {
        "Content-Type": "file/*; image/*",
        Accept: "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "DELETE, POST, GET, OPTIONS",
        "Access-Control-Allow-Headers":
          "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
      },
    });
    return filename;
  };

  const _clearData = () => {
    setAdverName("");
    setAdverNote("");
    setStartDate("");
    setEndDate("");
    setNoDateLimit(false);
    setIsEdited(false);
    setSelectedId("");
    setFile();
    setValidated(false);
  };

  const handleUpload = (event) => {
    const selected = event.target.files?.[0];
    if (selected) setFile(selected);
    event.target.value = "";
  };

  const handleSubmitAdvertising = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    setValidated(true);
    if (form.checkValidity() === false) return;
    if (!file) {
      showWarning("ກະລຸນາເລືອກຮູບແບນເນີກ່ອນ!");
      return;
    }
    handleCreateAdvertising();
  };

  const handleCreateAdvertising = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const filename = await uploadImage(file);
      await createAdvertising({
        variables: {
          data: {
            name: adverName,
            image: filename,
            note: adverNote,
            shop: shopId,
            startDate: noDateLimit ? null : startDate,
            endDate: noDateLimit ? null : endDate,
            status: false,
            device: device,
          },
        },
      });
      showSuccess("ເພີ່ມແບນເນີສຳເລັດແລ້ວ");
      setShowAddModal(false);
      _clearData();
      refetch();
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const _startEdit = (banner) => {
    setIsEdited(true);
    setSelectedId(banner?.id);
    setAdverName(banner?.name || "");
    setStartDate(
      banner?.startDate ? moment(banner.startDate).format("YYYY-MM-DD") : "",
    );
    setEndDate(
      banner?.endDate ? moment(banner.endDate).format("YYYY-MM-DD") : "",
    );
    setNoDateLimit(!banner?.startDate && !banner?.endDate);
    setFile();
  };

  const _updateAdvertising = async (id) => {
    if (saving) return;
    if (!adverName) {
      showWarning("ກະລຸນາໃສ່ຂໍ້ມູນໃຫ້ຄົບກ່ອນ!");
      return;
    }
    setSaving(true);
    try {
      const updateData = { name: adverName };
      if (noDateLimit) {
        updateData.startDate = null;
        updateData.endDate = null;
      } else {
        if (startDate) updateData.startDate = startDate;
        if (endDate) updateData.endDate = endDate;
      }
      // ປ່ຽນຮູບສະເພາະຕອນເລືອກຮູບໃໝ່
      if (file) updateData.image = await uploadImage(file);
      await updateAdvertising({
        variables: {
          data: updateData,
          where: { id },
        },
      });
      showSuccess("ແກ້ໄຂແບນເນີສຳເລັດແລ້ວ");
      _clearData();
      refetch();
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  // ເປີດ / ປິດ ການສະແດງຂອງຮູບໂຄສະນາ
  const _toggleStatus = async (banner) => {
    if (togglingId) return;
    setTogglingId(banner.id);
    try {
      const nextStatus = !banner.status;
      await updateAdvertising({
        variables: {
          data: { status: nextStatus },
          where: { id: banner.id },
        },
      });
      showSuccess(nextStatus ? "ປິດການໃຊ້ງານແລ້ວ" : "ເປີດການໃຊ້ງານແລ້ວ");
      refetch();
    } catch (error) {
      showError(error);
    } finally {
      setTogglingId(undefined);
    }
  };

  const _deleteAdvertising = async () => {
    try {
      await deleteAdvertising({ variables: { where: { id: selectedId } } });
      showSuccess("ລຶບແບນເນີສຳເລັດແລ້ວ");
      setShowDeleteModal(false);
      setSelectedId("");
      refetch();
    } catch (error) {
      showError(error);
    }
  };

  const ImageThumb = ({ image }) => {
    return (
      <img
        src={URL.createObjectURL(image)}
        alt={image.name}
        style={{
          objectFit: "contain",
          width: "100%",
          height: "100%",
          maxHeight: "400px",
        }}
      />
    );
  };

  const switchProps = {
    onColor: "#bfbdc7",
    onHandleColor: "#422f90",
    handleDiameter: 20,
    uncheckedIcon: false,
    checkedIcon: false,
    boxShadow: "0px 1px 5px rgba(0, 0, 0, 0.6)",
    activeBoxShadow: "0px 0px 1px 10px rgba(0, 0, 0, 0.2)",
    height: 20,
    width: 38,
    className: "react-switch",
  };

  return (
    <div>
      <ModalConfirmComponent
        showConfirmModal={showDeleteModal}
        handleCancel={() => setShowDeleteModal(false)}
        loadingConfirm={deleteLoading}
        handleConfirm={_deleteAdvertising}
        text="ທ່ານຕ້ອງການລຶບແບນເນີແທ້ບໍ?"
      />

      <Modal
        show={showAddModal}
        onHide={() => {
          if (saving) return;
          setShowAddModal(false);
          _clearData();
        }}
        keyboard={false}
        size="lg"
      >
        <Modal.Header
          style={{
            background: COLOR_APP,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: 50,
            color: "#fff",
          }}
        >
          <h5>ເພີ່ມແບນເນີໂຄສະນາ ({title})</h5>
        </Modal.Header>
        <Modal.Body
          style={{ display: "flex", flexDirection: "column", width: "100%" }}
        >
          <Form
            noValidate
            validated={validated}
            onSubmit={handleSubmitAdvertising}
          >
            <Form.Group controlId={`bannerName-${device}`}>
              <Form.Label>ຊື່ແບນເນີ</Form.Label>
              <Form.Control
                onChange={(e) => setAdverName(e?.target?.value)}
                required
                className="mb-2"
                type="text"
                placeholder="ຊື່ແບນເນີ..."
                value={adverName || ""}
              />
              <Form.Control.Feedback type="invalid">
                <small style={{ color: "red" }}>ກະລຸນາປ້ອນຊື່ແບນເນີກ່ອນ!</small>
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Check
              type="checkbox"
              id={`banner-no-date-${device}`}
              className="mb-2"
              label="ບໍ່ກຳນົດວັນທີ (ສະແດງຕະຫຼອດ)"
              checked={noDateLimit}
              onChange={(e) => setNoDateLimit(e.target.checked)}
            />
            <Row>
              <Col>
                <Form.Group controlId={`bannerStartDate-${device}`}>
                  <Form.Label>ວັນທີສ້າງ</Form.Label>
                  <Form.Control
                    onChange={(e) => setStartDate(e?.target?.value)}
                    required={!noDateLimit}
                    disabled={noDateLimit}
                    className="mb-2"
                    type="date"
                    value={noDateLimit ? "" : startDate || ""}
                  />
                  <Form.Control.Feedback type="invalid">
                    <small style={{ color: "red" }}>ກະລຸນາປ້ອນວັນທີສ້າງ!</small>
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId={`bannerEndDate-${device}`}>
                  <Form.Label>ວັນທີສິ້ນສຸດ</Form.Label>
                  <Form.Control
                    onChange={(e) => setEndDate(e?.target?.value)}
                    required={!noDateLimit}
                    disabled={noDateLimit}
                    className="mb-2"
                    type="date"
                    value={noDateLimit ? "" : endDate || ""}
                  />
                  <Form.Control.Feedback type="invalid">
                    <small style={{ color: "red" }}>
                      ກະລຸນາປ້ອນວັນທີສິ້ນສຸດ!
                    </small>
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group>
              <Form.Label>ລາຍລະອຽດ</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                onChange={(e) => setAdverNote(e?.target?.value)}
                className="mb-2"
                placeholder="ລາຍລະອຽດ..."
                value={adverNote || ""}
              />
            </Form.Group>
            <div style={{ textAlign: "center" }}>
              <input
                type="file"
                accept="image/*"
                id={`banner-file-upload-${device}`}
                onChange={handleUpload}
                hidden
              />
              <label
                htmlFor={`banner-file-upload-${device}`}
                className="w-100"
              >
                <div
                  style={{
                    background: "#E4E4E4E4",
                    width: isMobileSection ? "min(100%, 400px)" : "100%",
                    margin: "0 auto",
                    height: previewAspect ? undefined : "150px",
                    aspectRatio: previewAspect,
                    overflow: "hidden",
                    borderRadius: 8,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {file ? (
                    <ImageThumb image={file} />
                  ) : (
                    <div
                      style={{ color: "#fff", fontSize: 100, fontWeight: "bold" }}
                    >
                      +
                    </div>
                  )}
                </div>
              </label>
              <div>
                <span>{sizeHint}</span>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "2em",
              }}
            >
              <Button
                type="submit"
                disabled={saving}
                style={{
                  width: "180px",
                  marginRight: 10,
                  background: COLOR_APP,
                  fontWeight: "bold",
                }}
              >
                {saving ? <Spinner size="sm" animation="border" /> : "ບັນທຶກ"}
              </Button>
              <Button
                disabled={saving}
                onClick={() => {
                  setShowAddModal(false);
                  _clearData();
                }}
                style={{
                  width: "180px",
                  border: "1px solid " + COLOR_APP_CANCEL,
                  background: "transparent",
                  color: COLOR_APP_CANCEL,
                }}
              >
                ຍົກເລີກ
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <div style={{ height: 20 }} />
      <div
        style={{
          display: "flex",
          flexDirection: width > 700 ? "row" : "column",
          justifyContent: "space-between",
          alignItems: width > 700 ? "center" : "stretch",
          gap: 8,
          padding: "0 1em",
        }}
      >
        <h4 style={{ color: "#616161", margin: 0 }}>
          {title} [{loading && !data ? "..." : banners.length}]
        </h4>
        <Button
          onClick={() => {
            _clearData();
            setShowAddModal(true);
          }}
          style={{ background: COLOR_APP }}
        >
          <MdAdd style={{ fontSize: 23 }} />
          &nbsp;ເພີ່ມເແບນເນີ
        </Button>
      </div>
      <div style={{ padding: 20 }}>
        <Table responsive>
          <thead>
            <tr style={{ whiteSpace: "nowrap" }}>
              <th style={{ borderBottom: "none" }}>ລ/ດ</th>
              <th style={{ borderBottom: "none" }}>ຮູບເແບນເນີ</th>
              <th style={{ borderBottom: "none" }}>ຊື່ເແບນເນີ</th>
              <th style={{ borderBottom: "none" }}>ວັນທີສ້າງ</th>
              <th style={{ borderBottom: "none" }}>ວັນທີສິ້ນສຸດ</th>
              <th style={{ borderBottom: "none" }}>ການໃຊ້ງານ</th>
              <th style={{ borderBottom: "none" }}>ຈັດການ</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr>
                <td colSpan={7}>
                  <div
                    style={{
                      width: "100%",
                      height: "30vh",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <LoadingSpinner />
                  </div>
                </td>
              </tr>
            ) : banners.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">
                  ບໍ່ມີຂໍ້ມູນ
                </td>
              </tr>
            ) : (
              banners.map((banner, index) => {
                const editing = isEdited && selectedId === banner?.id;
                return (
                  <tr key={banner?.id} style={{ whiteSpace: "nowrap" }}>
                    <td>{index + 1}</td>
                    <td>
                      {editing ? (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            id={`banner-file-edit-${device}`}
                            onChange={handleUpload}
                            hidden
                          />
                          <label
                            htmlFor={`banner-file-edit-${device}`}
                            style={{ margin: 0 }}
                          >
                            <div
                              style={{
                                background: "#E4E4E4E4",
                                height: 60,
                                width: 100,
                                borderRadius: 8,
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                overflow: "hidden",
                              }}
                            >
                              {file ? (
                                <ImageThumb image={file} />
                              ) : banner?.image ? (
                                <img
                                  src={S3_URL + banner.image}
                                  style={{ borderRadius: 8, height: 60, width: 100 }}
                                  alt="imageAver"
                                />
                              ) : (
                                <div
                                  style={{
                                    color: "#616161",
                                    fontSize: 20,
                                    fontWeight: "bold",
                                  }}
                                >
                                  +
                                </div>
                              )}
                            </div>
                          </label>
                        </>
                      ) : (
                        <div
                          style={{
                            background: "#eee",
                            height: 60,
                            width: 100,
                            borderRadius: 8,
                            display: "flex",
                          }}
                        >
                          {banner?.image && (
                            <img
                              src={S3_URL + banner.image}
                              style={{ borderRadius: 8, height: 60, width: 100 }}
                              alt="imageAver"
                            />
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <Form.Control
                          onChange={(e) => setAdverName(e?.target?.value)}
                          type="text"
                          placeholder="ຊື່ເແບນເນີ..."
                          value={adverName}
                        />
                      ) : (
                        banner?.name
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <>
                          <Form.Control
                            onChange={(e) => setStartDate(e.target.value)}
                            type="date"
                            disabled={noDateLimit}
                            value={noDateLimit ? "" : startDate || ""}
                          />
                          <Form.Check
                            type="checkbox"
                            id={`banner-no-date-edit-${device}`}
                            className="mt-1"
                            label="ບໍ່ກຳນົດວັນທີ"
                            checked={noDateLimit}
                            onChange={(e) => setNoDateLimit(e.target.checked)}
                          />
                        </>
                      ) : banner?.startDate ? (
                        moment(banner.startDate).format("DD/MM/YYYY  HH:mm")
                      ) : (
                        "ບໍ່ກຳນົດ"
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <Form.Control
                          onChange={(e) => setEndDate(e.target.value)}
                          type="date"
                          disabled={noDateLimit}
                          value={noDateLimit ? "" : endDate || ""}
                        />
                      ) : banner?.endDate ? (
                        moment(banner.endDate).format("DD/MM/YYYY  HH:mm")
                      ) : (
                        "ບໍ່ກຳນົດ"
                      )}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "start",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Switch
                          {...switchProps}
                          onChange={() => _toggleStatus(banner)}
                          checked={banner?.status !== true}
                          disabled={togglingId === banner?.id}
                        />
                        <span>{banner?.status === true ? "ປິດ" : "ເປີດ"}</span>
                      </div>
                    </td>
                    {editing ? (
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button
                          className="btn hoverme"
                          onClick={() => _updateAdvertising(banner?.id)}
                          disabled={saving}
                          style={{ borderColor: COLOR_APP, color: COLOR_APP }}
                        >
                          {saving ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            <>
                              <FontAwesomeIcon
                                icon={faSave}
                                style={{ color: COLOR_APP }}
                              />{" "}
                              ບັນທຶກ
                            </>
                          )}
                        </button>
                        <button
                          className="btn hoverme ml-2"
                          disabled={saving}
                          style={{
                            borderColor: COLOR_APP_CANCEL,
                            color: COLOR_APP_CANCEL,
                          }}
                          onClick={_clearData}
                        >
                          <FontAwesomeIcon
                            icon={faTimes}
                            style={{ color: COLOR_APP_CANCEL }}
                          />{" "}
                          ຍົກເລີກ
                        </button>
                      </td>
                    ) : (
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button
                          className="btn hoverme"
                          onClick={() => _startEdit(banner)}
                        >
                          <FontAwesomeIcon
                            icon={faEdit}
                            style={{ color: COLOR_APP }}
                          />
                        </button>
                        <button
                          className="btn hoverme"
                          onClick={() => {
                            setSelectedId(banner?.id);
                            setShowDeleteModal(true);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTrashAlt}
                            style={{ color: "red" }}
                          />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function Advertising() {
  const { addToast } = useToasts();
  const { history } = useReactRouter();
  const { width } = useWindowDimensions();
  const shopId = localStorage.getItem("SHOP");

  // copy link
  const handleCopyLink = async () => {
    const _shopId = JSON.parse(localStorage.getItem("SHOP_DATA"))?.id || shopId;
    const url = LINK_SALE_PAGE + `/shop/${_shopId}`;
    try {
      await copy(url);
      addToast("ກ໋ອບປີ່ລິ້ງສຳເລັດແລ້ວ!", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      console.error("Copy failed: ", error);
    }
  };

  return (
    <div>
      <div style={{ height: 20 }} />
      <Breadcrumb>
        <Breadcrumb.Item onClick={() => history.replace("/settings")}>
          ຕັ້ງຄ່າ
        </Breadcrumb.Item>
        <Breadcrumb.Item active>ຕັ້ງຄ່າເຊວເພຈ</Breadcrumb.Item>
      </Breadcrumb>
      <div style={{ height: 10 }} />
      <div
        style={{
          display: "flex",
          flexDirection: width > 700 ? "row" : "column",
          justifyContent: "space-between",
          gap: 8,
          padding: "0 1em",
        }}
      >
        <h3
          style={{
            color: "#616161",
            fontSize: width > 700 ? "1.75em" : "1.5em",
          }}
        >
          ຮູບໂຄສະນາສິນຄ້າໃນ ເຊວເພຈ
        </h3>
        <Button
          style={{
            background: "#F7F7F7",
            color: "#422F90",
            border: "1px solid #422F90 ",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onClick={handleCopyLink}
        >
          <BsLink style={{ fontSize: 26 }} />{" "}
          <span> ສຳເນົາລິ້ງ ເຊວເພຈ</span>
        </Button>
      </div>

      <Tabs
        id="advertising-device-tabs"
        defaultActiveKey={DEVICE_DESKTOP}
        className="mt-3 px-3"
      >
        <Tab eventKey={DEVICE_DESKTOP} title="ໜ້າຈໍໃຫຍ່">
          <AdvertisingSection
            device={DEVICE_DESKTOP}
            title="ສຳລັບໜ້າຈໍໃຫຍ່"
            sizeHint="ເລືອກຮູບເແບນເນີ ທີ່ມີຂະໜາດກ້ວາງປະມານ (1024px X 340px)"
            shopId={shopId}
            width={width}
          />
        </Tab>
        <Tab eventKey={DEVICE_MOBILE} title="ໜ້າຈໍນ້ອຍ / ໂທລະສັບ (16:9)">
          <AdvertisingSection
            device={DEVICE_MOBILE}
            title="ສຳລັບໜ້າຈໍນ້ອຍ / ໂທລະສັບ (16:9)"
            sizeHint="ເລືອກຮູບເແບນເນີ ອັດຕາສ່ວນ 16:9 (ເຊັ່ນ 1920px X 1080px)"
            shopId={shopId}
            width={width}
          />
        </Tab>
      </Tabs>
    </div>
  );
}

export default Advertising;
